/**
 * Swatch functionality for product variant selection.
 * Handles swatch click events, variant updates, image gallery switching,
 * availability checking, and recommendations updates on product pages.
 * 
 * @description This module provides:
 * - Swatch click handling and variant selection
 * - Image gallery updates based on variant
 * - Variant availability checking and UI updates
 * - Quantity limit enforcement
 * - Product recommendations updates
 * 
 * @requires jQuery (for slick carousel and event binding)
 * 
 * @example
 * // Automatically initializes on page load
 * swatches.init();
 */
let swatches = {
  /** @type {boolean} Tracks if swatches have been initialized */
  initialized: false,

  /**
   * Initializes the swatch functionality by binding events and checking initial state.
   * 
   * @returns {void}
   * @sideEffect Binds event listeners, checks variant availability
   */
  init() {
    let _this = this;

    // Initial availability check and quantity update
    _this.checkSwatchVariantAvailability();
    _this.updateMaxQty();

    // Swatch carousel thumbnail click
    $(document).on('click', '.swatches-carousel .thumbnail', function (e) {
      e.preventDefault();
      let mediaId = $(this).get(0).dataset.mediaId;
      let targetMedia = document.querySelector(`.product-slideshow .slick-slide[data-media-id="${mediaId}"]`);
      let slickSlider = targetMedia.closest('.slick-slider');

      let slickIndex = targetMedia.dataset.slickIndex;
      $(slickSlider).slick('slickGoTo', slickIndex);
    });

    // Swatch option click
    $(document).on('click', '.js-option.swatch', function (e) {
      _this.handleSwatchClick(e.target);
    });

    // Regular option change (for non-swatch selectors)
    $(document).on('click', '.js-option', function (e) {
      setTimeout(() => {
        let popup = false;
        // Check if in popup context
        if(e.target.closest('.cc-popup-content')) {
          popup = true;
        }
        _this.checkSwatchVariantAvailability(popup);
        _this.updateMaxQty();
      }, .5);
    });

    // Swatch product block image navigation
    $('.swatch-product-block .image-page-button').on('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      _this.changeSwatchProductBlockImage(e.target);
    });

    // Quantity input change
    if (document.querySelector('.swatch-product-form input[name="quantity"]')) {
      document.querySelector('.swatch-product-form input[name="quantity"]').addEventListener('change', (e) => {
        _this.updateMaxQty();
        let maxQty = parseInt(e.target.getAttribute('max'));
        if (parseInt(e.target.value) > maxQty) e.target.value = maxQty;
      });
    }

    // Handle inactivated product blocks
    if (document.querySelector('product-block .product-block__image--inactivated')) {
      document.querySelectorAll("product-block .product-block__image--inactivated").forEach(function (el) {
        el.classList.remove('product-block__image--inactivated');
        if(el.querySelector('img')) {
          el.querySelector('img').setAttribute('src', el.dataset.src);
        }
      });
    }

    // Auto-select default variant on page load
    $(function() {
      if (document.querySelector('.product-form.off-card-container .js-option.swatch.default-var') != null) {
        document.querySelectorAll('.product-form.off-card-container .js-option.swatch.default-var').forEach((el) => {
          $(el).trigger('click');
        })
      }
    });

    // Clean up quick buy modals when closed
    document.addEventListener('quickbuy:closed', () => {
      document.querySelectorAll('.quickbuy-modal').forEach((el) => {
        el.remove();
      })
    });

    // Initialize default variant in quick buy popup
    document.addEventListener('quickbuy:opened', () => {
      if (document.querySelector('.cc-popup-content .quickbuy-content .product-form .js-option.swatch.default-var') != null) {
        document.querySelectorAll('.cc-popup-content .quickbuy-content .product-form .js-option.swatch.default-var').forEach((el) => {
          $(el).trigger('click');

          // Enable add to cart button if variant available
          let targetVariant = document.querySelector('.cc-popup-content .quickbuy-content .product-form .option-selector:not(.swatch-selector):not(.hidden) .js-option:checked');
          if(targetVariant) {
            if(!targetVariant.classList.contains('is-unavailable')) {
              let targetBtn = document.querySelector('.cc-popup-content .quickbuy-content .product-form .quantity-submit-row  .button');
              if(targetBtn) {
                targetBtn.removeAttribute('disabled');
                if(targetBtn.dataset.addToCartText) {
                  targetBtn.innerText = targetBtn.dataset.addToCartText;
                }
              }
            }
          }
        })
      }
    });
  },

  /**
   * Changes the product block image on collection pages when pagination buttons clicked.
   * 
   * @param {HTMLElement} el - The button element clicked (next/previous)
   * @returns {void}
   * @sideEffect Updates active/h hover images in gallery
   */
  changeSwatchProductBlockImage(el) {
    let cont = el.closest('.image-cont');
    if (!cont) return;
    
    let currentGallery = cont.querySelector('.image-label-wrap__images:not(.hidden)');
    let currentImg = currentGallery.querySelector('.product-block__image--active');

    // Reset all inactivated images
    if (currentGallery) {
      currentGallery.querySelectorAll('.product-block__image--inactivated').forEach(el => {
        el.classList.remove('product-block__image--inactivated');
      });
    }

    if (currentImg) {
      let currentIndex = parseInt(currentImg.dataset.imageIndex);
      let nextIndex = currentIndex + 1;

      // Handle previous button
      if (el.classList.contains('image-page-button--previous')) {
        nextIndex = currentIndex - 1;
      }

      // Wrap around indices
      if (nextIndex < 0) nextIndex = currentGallery.querySelectorAll('.product-block__image').length;
      if (nextIndex >= currentGallery.querySelectorAll('.product-block__image').length) nextIndex = 0;

      // Calculate hover image index
      let nextNextIndex = nextIndex + 1;
      if (nextNextIndex < 0) nextNextIndex = currentGallery.querySelectorAll('.product-block__image').length;
      if (nextNextIndex > (currentGallery.querySelectorAll('.product-block__image').length - 1)) nextNextIndex = 0;

      // Get next images
      let nextImage = currentGallery.querySelector('[data-image-index="' + nextIndex + '"]');
      let hoverImage = currentGallery.querySelector('[data-image-index="' + nextNextIndex + '"]');

      // Update active image
      if (nextImage) {
        currentImg.classList.remove('product-block__image--active');
        nextImage.classList.add('product-block__image--active');
      }
      // Update hover image
      if (hoverImage) {
        currentGallery.querySelector('.product-block__image--show-on-hover').classList.remove('product-block__image--show-on-hover');
        hoverImage.classList.add('product-block__image--show-on-hover');
      }
    }
  },

  /**
   * Handles swatch click to update product variant, images, prices, and availability.
   * Main handler for swatch selection on PDP and PLP.
   * 
   * @param {HTMLElement} el - The clicked swatch element
   * @returns {void}
   * @sideEffect Updates URL, images, prices, ATC button state, dispatches events
   */
  handleSwatchClick(el) {
    let _this = this;
    let cont = el.closest('.option-selectors.swatches');
    let productCard = false;
    let swatchName = '';
    let prodDetail = el.closest('.product-detail');
    let unavailable = el.classList.contains('unavailable');
    let atcTarget = prodDetail.querySelector('.quantity-submit-row');
    let unavailableTarget = prodDetail.querySelector('.product-unavailable');

    // If we're on a PLP we have product cards and multiple swatches forms (one for leader and one for each swatch product)
    if (!cont && el.closest('.option-selector.variant-swatches')) {
      cont = el.closest('.option-selector.variant-swatches');
      productCard = el.closest('product-block');
      prodDetail = el.closest('.product-info');
      swatchName = cont.closest('product-block').dataset.radio;
    } else {
      swatchName = cont.dataset.radio;
    }
    if (productCard && !cont.closest('product-block')) return;

    // Find checked radio input
    let radio = cont.querySelector('input[name="' + swatchName + '"]:checked');
    if (radio == null) return;
    let id = radio.value;

    // Update product ID inputs if present
    if(document.querySelector('input[name="product-id"]')) {
      document.querySelectorAll('input[name="product-id"]').forEach(el => {
        el.value = id;
      })
    }

    // Get media and option data
    let mediaId = radio.dataset.mediaid;
    let swatchOptions = cont.querySelectorAll('.option-selector[data-swatch-id="' + id + '"]');
    let productListTarget = document.querySelector('.collection-slider-row[data-product-id="' + id + '"]');
    let styleNumberTarget = prodDetail.querySelector('.is-stylenumber .product-info-block[data-product-id="' + id + '"]');
    let titleClass = '.title';
    let galleryClass = '.gallery';

    // Update URL with product handle
    let handle = el.dataset.handle;
    window.history.replaceState(handle, 'handle', '/products/' + handle + window.location.search);

    // Update product card links
    if (productCard && typeof el.dataset.productUrl != "undefined") {
      productCard.querySelectorAll('.product-link').forEach(_productLink => {
        _productLink.setAttribute('href', el.dataset.productUrl);
      })
    }

    // Set correct selectors for card vs detail
    if (productCard) {
      titleClass = '.product-block__title';
      galleryClass = '.image-label-wrap__images';
    }
    
    // Find target elements
    let titleTarget = prodDetail.querySelector(titleClass + '[data-product-id="' + id + '"]');
    let galleryTarget = document.querySelector(galleryClass + '[data-product-id="' + id + '"]');

    // Handle product card-specific elements
    if (productCard) {
      galleryTarget = productCard.querySelector(galleryClass + '[data-product-id="' + id + '"]');
      let priceTarget = productCard.querySelector('.product-price[data-product-id="' + id + '"]');

      // Toggle price visibility
      productCard.querySelectorAll('.product-price').forEach(_el => {
        _el.classList.add('hidden');
      });
      priceTarget.classList.remove('hidden');
    }

    // Update thumbnail selection
    let targetThumb = prodDetail.querySelector(`.thumbnail[data-media-id="${mediaId}"]`);
    if (targetThumb && prodDetail.querySelector('.thumbnail.selected')) {
      targetThumb.classList.add('selected');
    }

    // Toggle product list visibility
    if (productListTarget) {
      document.querySelectorAll('.collection-slider-row').forEach(_el => {
        _el.classList.add('hidden');
      });

      // Show if has blocks and not current product
      if(productListTarget.querySelectorAll('product-block').length < 1 ||
        (productListTarget.querySelectorAll('product-block').length == 1 && productListTarget.querySelector('product-block').dataset.productHandle == handle)) {
        productListTarget.classList.add('hidden');
      }
      else {
        productListTarget.classList.remove('hidden');
      }
    }

    // Toggle style number visibility
    if (styleNumberTarget) {
      prodDetail.querySelectorAll('.is-stylenumber .product-info-block').forEach(_el => {
        _el.classList.add('hidden');
      });
      styleNumberTarget.classList.remove('hidden');
    }

    // Toggle title visibility
    if (titleTarget) {
      prodDetail.querySelectorAll(titleClass).forEach(_el => {
        _el.classList.add('hidden');
      });
      titleTarget.classList.remove('hidden');
    }

    // Hide non-selected option selectors
    cont.querySelectorAll('.option-selector:not(.swatch-selector)').forEach(_el => {
      _el.classList.add('hidden');
    });

    // Show relevant swatch options
    if (swatchOptions.length > 0) {
      swatchOptions.forEach(_el => {
        if (_el.getAttribute('aria-labelledby').includes('Title-Label')) {
          _el.classList.add('hidden');
        } else {
          _el.classList.remove('hidden');
        }
      });
    }

    // Gallery/ATC updates can throw (missing slick arrows, slideshow, etc.).
    // Always dispatch swatch-clicked so required logos still pre-select.
    try {
      // Update gallery
      if (galleryTarget) {
        if (productCard) {
          // Product card gallery update
          productCard.querySelectorAll(galleryClass).forEach(_el => {
            _el.classList.add('hidden');
          });
          let i = 0;
          galleryTarget.querySelectorAll('img').forEach(el => {
            if (i < 1) el.closest('.product-block__image').classList.add('product-block__image--active');
            let src = el.dataset.src;
            el.setAttribute('src', src);
            el.classList.add('loaded');
            i++;
          })
        } else {
          // PDP gallery update
          prodDetail.querySelectorAll(galleryClass).forEach(_el => {
            _el.classList.add('hidden');
            const arrows = _el.querySelector('.slideshow-controls__arrows');
            if (arrows) {
              arrows.querySelectorAll('.slick-arrow').forEach(_el => {
                _el.remove();
              });
            }
          });
        }

        galleryTarget.classList.remove('hidden');

        // Reinitialize slick on PDP
        const slideshowEl = $('.gallery[data-product-id="' + id + '"] .slideshow')[0];
        if (!productCard && slideshowEl && slideshowEl.dataset.slick) {
          let dataSlick = JSON.parse(slideshowEl.dataset.slick);
          dataSlick.appendArrows = galleryTarget.querySelector('.slideshow-controls__arrows');

          if($('.gallery[data-product-id="' + id + '"] .slideshow').hasClass('slick-slider')) {
            $('.gallery[data-product-id="' + id + '"] .slideshow').slick('unslick');
            $('.gallery[data-product-id="' + id + '"] .slideshow').slick(dataSlick);
          }
        }
      }

      // Toggle ATC button based on availability
      if (atcTarget && unavailableTarget) {
        if(unavailable) {
          atcTarget.classList.add('hidden');
          unavailableTarget.classList.remove('hidden');
        }
        else {
          atcTarget.classList.remove('hidden');
          unavailableTarget.classList.add('hidden');
          // Reset quantity to minimum
          let qtyInput = atcTarget.querySelector('.quantity-wrapper input');
          if (qtyInput) {
            let minQty = qtyInput.getAttribute('min');
            if(!minQty) minQty = 1;
            qtyInput.value = minQty;
          }
        }
      }
    } catch (err) {
      console.warn('swatches.handleSwatchClick UI update failed', err);
    }

    // Fetch recommendations after delay
    setTimeout(function() {
      try {
        _this.getRecommendations(id);
      } catch (err) {
        console.warn('swatches.getRecommendations failed', err);
      }
    },250 )

    // Mark as initialized and dispatch event
    this.initialized = true;
    document.dispatchEvent(new CustomEvent('swatch-clicked', {detail: {target: el}}));
  },

  /**
   * Fetches and updates product recommendations for the selected variant.
   * 
   * @param {number} id - Product ID for recommendations
   * @returns {void}
   * @sideEffect Replaces recommendations HTML in DOM
   */
  getRecommendations(id) {
    let cont = document.querySelector('.section-product-recommendations');
    let oldRecommendations = cont.querySelector('.product-recommendations');
    let oldRecommendationsParent = oldRecommendations.parentNode;
    let sectionId = cont.getAttribute('id').replace('shopify-section-template--','').replace('__recommendations','');

    let url = `/recommendations/products?section_id=template--${sectionId}__recommendations&limit=5&product_id=${id}`

    fetch(url)
      .then(res => res.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");

        const recommendations = doc.querySelector('.product-recommendations');
        if(recommendations) {
          // Add animation classes
          recommendations.classList.add('cc-animate-init')
          recommendations.classList.add('-in')
          recommendations.classList.add('cc-animate-complete')

          if(recommendations.querySelector('cc-carousel')) {
            recommendations.querySelector('cc-carousel').classList.add('cc-fade-in-up')
            recommendations.querySelector('cc-carousel').classList.add('cc-animate-init')
            recommendations.querySelector('cc-carousel').classList.add('-in')
            recommendations.querySelector('cc-carousel').classList.add('cc-animate-complete')
          }
        }

        // Replace old recommendations with new
        oldRecommendations.remove();
        oldRecommendationsParent.appendChild(recommendations);
      });
  },

  /**
   * Checks variant availability for swatch-selected products.
   * Updates option visibility, quantity limits, and ATC button state.
   * 
   * @param {boolean} [popup=false] - Whether checking popup context
   * @returns {void}
   * @sideEffect Updates quantity limits, ATC button, option availability classes
   */
  checkSwatchVariantAvailability(popup=false) {
    // Get container based on context
    let cont = document.querySelector('.product-form .option-selectors.swatches');
    if(popup) {
      cont = document.querySelector('.cc-popup-content .product-form .option-selectors.swatches');
    }
    if (!cont) return;
    
    let prodDetail = document.querySelector('.product-detail');
    let swatchName = cont.dataset.radio;
    let radio = cont.querySelector('input[name="' + swatchName + '"]:checked');
    if (radio == null) return;
    let id = radio.value;
    let swatchOptions = cont.querySelectorAll('.option-selector[data-swatch-id="' + id + '"]');
    let atcButton = document.querySelector('.product-form button[type="submit"]');
    let qtyTarget = document.querySelector('.quantity-wrapper input[name="quantity"]');
    let variants = window.swatchVariantsJSON;
    let currentVariants = variants[id];

    // Adjust for popup context
    if(popup) {
      atcButton = document.querySelector('.cc-popup-content .product-form button[type="submit"]');
      qtyTarget = document.querySelector('.cc-popup-content .quantity-wrapper input[name="quantity"]');
      prodDetail = document.querySelector('.cc-popup-content .product-detail');
    }

    // Get selected options
    let selectedOption1 = null;
    let selectedOption2 = null;

    let selectedOption1El = swatchOptions[0].querySelector('input:checked');
    if (selectedOption1El != null) selectedOption1 = selectedOption1El.value;
    let currentVariant = currentVariants[selectedOption1];

    // Handle dual-option variants
    if (swatchOptions.length === 2) {
      let selectedOption2El = swatchOptions[1].querySelector('input:checked');
      if (selectedOption2El != null) selectedOption2 = selectedOption2El.value;
      currentVariant = currentVariants[selectedOption1 + ' / ' + selectedOption2];
    }

    // Update quantity limit
    if (typeof currentVariant != "undefined" && typeof currentVariant.inventory_quantity != "undefined" && currentVariant && qtyTarget) {
      let maxQty = currentVariant.inventory_quantity;
      if (maxQty < 0) maxQty = 0;
      qtyTarget.setAttribute('max', maxQty);
      if (parseInt(qtyTarget.value) > maxQty) qtyTarget.value = maxQty;
    }

    // Update variant ID input and show correct price
    if (currentVariant != null) {
      document.querySelectorAll('input[name="id"]').forEach(el => {
        el.value = currentVariant.id;
      })

      let priceTarget = prodDetail.querySelector('.price-area[data-product-id="' + id + '"][data-variant-id="' + currentVariant.id + '"]');
      prodDetail.querySelectorAll('.price-area').forEach(_el => {
        _el.classList.add('hidden');
      });
      if(priceTarget) priceTarget.classList.remove('hidden');
    }

    // Handle default variant
    if (!currentVariant && atcButton && currentVariants.hasOwnProperty('Default Title')) {
      if (currentVariants['Default Title'].available) {
        let atcText = atcButton.dataset.addToCartText;
        atcButton.removeAttribute('disabled');
        atcText = atcButton.textContent = atcText;
      } else {
        atcButton.setAttribute('disabled', 'disabled');
        atcButton.textContent = 'Unavailable';
      }
      return;
    }

    // Track available options
    let availableOptions1 = [];
    let availableOptions2 = [];

    // Initialize option 2 as unavailable
    let opt2El = document.querySelector('.option-selector[data-option-index="1"][data-swatch-id="' + id + '"]');
    if (opt2El) {
      opt2El.querySelectorAll('input').forEach(el => {
        el.classList.add('is-unavailable');
      })
    }

    // Loop through variants to check availability
    for (const [key, value] of Object.entries(currentVariants)) {
      let _key = key.split('/');
      let option1 = _key[0].replace(' ', '');
      let option2 = null;
      let option1El = swatchOptions[0].querySelector('input[value="' + option1 + '"]');
      let option2El = null;

      if (typeof _key[1] != "undefined") {
        option2 = _key[1].replace(' ', '');
        option2El = swatchOptions[1].querySelector('input[value="' + option2 + '"]');
      }

      // Mark available options
      if (value.available) {
        if (!availableOptions1.includes(option1)) {
          availableOptions1.push(option1);
        }
        if (!availableOptions2.includes(option1)) {
          availableOptions2.push(option1);
        }

        if (option1El) {
          option1El.classList.remove('is-unavailable');
        }
        if (option2El && option1 == selectedOption1) {
          option2El.classList.remove('is-unavailable');
        }
      } else {
        // Mark unavailable options
        if (option1El && !availableOptions1.includes(option1)) {
          option1El.classList.add('is-unavailable');
        }
        if (option2El && option1 == selectedOption1 && !availableOptions2.includes(option2)) {
          option2El.classList.add('is-unavailable');
        }
      }

      // Update ATC button
      if (atcButton && currentVariant) {
        let atcText = atcButton.dataset.addToCartText;
        if (!currentVariant.available) {
          atcButton.setAttribute('disabled', 'disabled');
          atcText = 'Unavailable';
        } else {
          atcButton.removeAttribute('disabled');
        }
        atcButton.textContent = atcText;
      }
    }
  },

  /**
   * Updates maximum quantity based on selected variant inventory.
   * 
   * @returns {void}
   * @sideEffect Updates quantity input max attribute
   */
  updateMaxQty() {
    let variants = window.variantsJSON;
    if (typeof variants == "undefined" || !variants) return;

    let qtyTarget = document.querySelector('.swatch-product-form .quantity-wrapper input[name="quantity"]');
    let swatchOptions = document.querySelectorAll('.option-selector');
    let selectedOption1 = null;
    let selectedOption2 = null;

    // Get selected options
    let selectedOption1El = typeof swatchOptions[0] != 'undefined' ? swatchOptions[0].querySelector('input:checked') : null;
    if (selectedOption1El != null) selectedOption1 = selectedOption1El.value;
    let currentVariant = variants[selectedOption1];

    // Handle dual options
    if (swatchOptions.length === 2) {
      let selectedOption2El = typeof swatchOptions[0] != 'undefined' ? swatchOptions[1].querySelector('input:checked') : null;
      if (selectedOption2El != null) selectedOption2 = selectedOption2El.value;
      currentVariant = variants[selectedOption1 + ' / ' + selectedOption2];
    }

    // Update quantity limit
    if (typeof currentVariant != "undefined" && typeof currentVariant.inventory_quantity != "undefined" && currentVariant && qtyTarget) {
      let maxQty = parseInt(currentVariant.inventory_quantity);
      if (maxQty < 0) maxQty = 0;
      qtyTarget.setAttribute('max', maxQty);
      if (parseInt(qtyTarget.value) > maxQty) qtyTarget.value = maxQty;
    }
  }

};

// Initialize on page load
swatches.init();
